import { QueryKey } from "@tanstack/react-query";
import { GridSortDirection } from "@mui/x-data-grid-pro";
import { TabType } from "@features/assets-feature/types/TabsType";
import assetsAccessor from "@features/assets-feature/utils/assetsAccessor";

export const CACHE_KEY_ASSET_INVENTORY: QueryKey = ["asset_inventory"];
export const CACHE_KEY_PURCHASED_ASSETS_PICKLIST: QueryKey = [
  "purchased_assets_picklist",
];
export const CACHE_KEY_PURCHASED_ASSETS_SPEND_BY_ORDER: QueryKey = [
  "purchased_assets_spend_by_order",
];
export const CACHE_KEY_PURCHASED_ASSETS_SUMMARY: QueryKey = [
  "purchased_assets_summary",
];
export const CACHE_KEY_PURCHASED_ASSETS_SPEND_BY_MANUFACTURER: QueryKey = [
  "purchased_assets_spend_by_manufacturer",
];
export const CACHE_KEY_PURCHASED_ASSETS_SPEND_BY_MANUFACTURER_FILTERS: QueryKey =
  ["purchased_assets_spend_by_manufacturer_filters"];
export const CACHE_KEY_PURCHASED_ASSETS_SPEND_BY_CATEGORY: QueryKey = [
  "purchased_assets_spend_by_category",
];
export const CACHE_KEY_PURCHASED_ASSETS_SPEND_BY_CATEGORY_FILTERS: QueryKey = [
  "purchased_assets_spend_by_category_filters",
];
export const CACHE_KEY_ASSET_LOCATIONS: QueryKey = ["asset_locations"];
export const CACHE_KEY_PRODUCT_CATALOG: QueryKey = ["product_catalog"];
export const CACHE_KEY_END_USER_DEVICES: QueryKey = ["end_user_devices"];
export const CACHE_KEY_ASSET_LIFECYCLE: QueryKey = ["asset_lifecycle"];
export const CACHE_KEY_ASSET_DETAIL: QueryKey = ["asset_detail"];
export const CACHE_KEY_ASSET_HARDWARE_BY_AGE: QueryKey = [
  "asset_hardware_by_age",
];
export const CACHE_KEY_ASSET_CONTRACTS: QueryKey = ["asset_contracts"];
export const CACHE_KEY_ASSET_PICKLIST: QueryKey = ["asset_picklist"];
export const CACHE_KEY_ASSET_TAGS: QueryKey = ["asset_tags"];
export const CACHE_KEY_WAREHOUSE_STOCK: QueryKey = ["warehouse_stock"];
export const CACHE_KEY_WAREHOUSE_ORDERS: QueryKey = ["warehouse_orders"];

export const PurchasedAssetsTabIds = {
  all: "All",
  hardware: "Hardware",
  softwareAndSubscriptions: "Software & Subscriptions",
};

export const PurchasedAssetsViewByTabIds = {
  purchaseDate: "Order",
  manufacturer: "Manufacturer",
  category: "Category",
};

export const DiscoveredAssetTypeFilterTabIds = {
  all: "All",
  hardware: "Hardware",
  softwareAndSubscriptions: "Software & Subscriptions",
  cloudResources: "Cloud Resources",
};

export const DiscoveredAssetTypeFilterValues = {
  all: [],
  hardware: ["Hardware"],
  softwareAndSubscriptions: ["Software", "Subscription"],
  cloudResources: ["Digital", "Data"],
};

export const DiscoveredAssetsTabPaths = {
  byAsset: "by-asset",
  byProduct: "by-product",
  byLocation: "by-location",
  byContract: "by-contract",
  eolTimeline: "eol-timeline",
} as const;

export const InventoryTabIds = {
  all: "all",
  hardwareConsumables: "hardware-consumables",
  softwareSubscription: "software-subscription",
  dataDigital: "data-digital",
};
export const AssetInventoryTabs: TabType[] = [
  { tabId: InventoryTabIds.all, tabName: "All" },
  {
    tabId: InventoryTabIds.hardwareConsumables,
    tabName: "Hardware & Consumables",
  },
  {
    tabId: InventoryTabIds.softwareSubscription,
    tabName: "Software & Subscriptions",
  },
  {
    tabId: InventoryTabIds.dataDigital,
    tabName: "Data & Digital",
  },
];

export const InventoryInitialDataGrid = {
  initialPageSize: 25,
  initialSortField: assetsAccessor.displayName,
  initialSortDirection: "desc" as GridSortDirection,
};
export const featureFlags = {
  assetV2: "assets.v2-UI",
};

export const AssetDialogTabs = {
  details: "details",
  contracts: "contracts",
  children: "children",
  tagsAndNotes: "tagsAndNotes",
  activity: "activity",
  support: "support",
  locations: "locations",
};

export const ManagedBy = {
  shi: "Managed by SHI",
  other: "Managed by Other Partner",
};

export const KnoxWareHouse = {
  name: "Knox",
  displayName: "SHI - Knox Warehouse",
};
export const RidgeWareHouse = {
  name: "Ridge",
  displayName: "SHI - Ridge Warehouse",
};

export const WarehouseOrderType = {
  pickNShip: "Pick & Ship",
  config: "Config",
  hlm: "HLM",
  kit: "Kit",
  rack: "Rack",
  miniRack: "Mini Rack",
};

export const GridDefinitionConstants = {
  shi: "shi",
  shiComplete: "SHI Complete",
};

export const untaggedTag = "Untagged";

export const assetDatasource = {
  CRR: "Cisco Ready Report",
  CC: "Contract Coverage",
  SNTC: "SmartNet Total Care",
  ciscoSubscriptionManagement: "Subscription Management API",
  ciscoEA: "Enterprise Agreement API",
  manual: "Manual Upload",
};
export const datasourceWithSupport = new Set([
  assetDatasource.CRR,
  assetDatasource.CC,
  assetDatasource.SNTC,
  assetDatasource.ciscoEA,
  assetDatasource.ciscoSubscriptionManagement,
]);

export const assetContextProviders = {
  assetInventory: "assetInventory",
  assetDashboard: "assetDashboard",
};

export const assetPaths = {
  dashboard: "assets-dashboard",
};

export interface ExportColumn {
  header: string;
  dataField: string;
  format?: "string" | "date";
}

type ViewBy = "order" | "manufacturer" | "category";
type ActiveTab = "all" | "hardware" | "softwareAndSubscriptions";

type PurchasedAssetsExportColumns = {
  [K in ViewBy]: {
    [T in ActiveTab]: ExportColumn[];
  };
};

export const PURCHASED_ASSETS_EXPORT_COLUMNS: PurchasedAssetsExportColumns = {
  order: {
    all: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Sales Order", dataField: "orderNumber" },
      { header: "PO Number", dataField: "poNumber" },
      { header: "Purchase Date", dataField: "orderDate", format: "date" },
      { header: "Asset Type", dataField: "assetType" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Category", dataField: "category" },
      { header: "Sub Category", dataField: "subCategory" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
      {
        header: "Event Date",
        dataField: "compellingEventDate",
        format: "date",
      },
      { header: "Serial Number(s)", dataField: "serialNumber" },
    ],
    hardware: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Sales Order", dataField: "orderNumber" },
      { header: "PO Number", dataField: "poNumber" },
      {
        header: "Refresh Date",
        dataField: "compellingEventDate",
        format: "date",
      },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Category", dataField: "category" },
      { header: "Sub Category", dataField: "subCategory" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
      { header: "Serial Number(s)", dataField: "serialNumber" },
    ],
    softwareAndSubscriptions: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Sales Order", dataField: "orderNumber" },
      { header: "PO Number", dataField: "poNumber" },
      {
        header: "Renewal Date",
        dataField: "compellingEventDate",
        format: "date",
      },
      { header: "Asset Type", dataField: "assetType" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
      { header: "Serial Number(s)", dataField: "serialNumber" },
    ],
  },
  manufacturer: {
    all: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Asset Type", dataField: "assetType" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Category", dataField: "category" },
      { header: "Sub Category", dataField: "subCategory" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
    ],
    hardware: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Category", dataField: "category" },
      { header: "Sub Category", dataField: "subCategory" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
    ],
    softwareAndSubscriptions: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Asset Type", dataField: "assetType" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
    ],
  },
  category: {
    all: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Asset Type", dataField: "assetType" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Category", dataField: "category" },
      { header: "Sub Category", dataField: "subCategory" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
    ],
    hardware: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Category", dataField: "category" },
      { header: "Sub Category", dataField: "subCategory" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
    ],
    softwareAndSubscriptions: [
      { header: "Product", dataField: "description" },
      { header: "Part Number", dataField: "shiPartNumber" },
      { header: "Asset Type", dataField: "assetType" },
      { header: "Manufacturer", dataField: "manufacturerName" },
      { header: "Total Quantity", dataField: "totalQuantity" },
      { header: "Total Spend", dataField: "totalSpend" },
    ],
  },
};
